<template>
  <div>
    <page-title value="Child Diary"></page-title>

    <v-card variant="flat" class="mt-6 pa-2" color="grey-lighten-5">
      <v-container fluid>
        <v-row align="center">
          <v-col cols="4">
            <h5>{{ $t('common.class') }}</h5>
            <v-select
              v-model="classFilterValue"
              :items="classesWithAllOption" 
              :item-title="(item) => item.classId === 'all' ? item.className : `(${item.year}) ${item.className}`" 
              item-value="classId"
              variant="outlined"
              hide-details
              density="compact"
              clearable
              :disabled="isFetching"
            ></v-select>
          </v-col>
          <v-col cols="4">
            <h5>{{ $t('common.date') }}</h5>
            <date-picker v-model="dateFilterValue"></date-picker>
          </v-col>
          <v-col cols="4">
            <v-btn
              class="mt-4 search-button"
              color="primary"
              variant="flat"
              size="large" :disabled="!isSearchValid"
              :loading="isFetching"
              @click="searchBtn"
            >
              {{ $t('common.search') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <v-table class="mt-4">
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">Child Name</th>
            <th class="text-left">Class</th>
            <th class="text-left">Date</th>
            <th class="text-left">Gender</th>
            <th class="text-left">Attendance</th>
            <th class="text-left"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="d in diaries" :key="d.child.childId">
            <td>{{ d.child.lastName }} {{ d.child.firstName }}</td>
            <td>{{ d.diary ? getClassName(d.diary) : getChildClassName(d.child) }}</td>
            <td>{{ formatDate(d.diary ? d.diary?.date : preDateFilterValue) }}</td>
            <td>{{ d.child.gender === "male" ? $t('common.male') : $t('common.female') }}</td>
            <td>{{ d.diary ? getAttendance(d.diary) : getChildAttendance(d.child) }}</td>
            <td v-if="d.diary">
              <v-btn
                color="primary"
                variant="flat"
                width="100px"
                :to="`/dailyreport/child/${d.child.childId}/${d.diary.date}`"
                :disabled="isFetching"
                >{{ $t('common.edit') }}</v-btn
              >
            </td>
            <td v-else>
              <v-btn
                color="primary"
                variant="outlined"
                width="100px"
                @click="newDiary(d.child.childId, preDateFilterValue)"
                :disabled="isFetching"
                >{{ $t('common.create') }}</v-btn
              >
            </td>
          </tr>
        </tbody>
      </template>
    </v-table>

    <v-pagination
      class="pt-4"
      v-model="page"
      @update:modelValue="pageClick" :length="Math.ceil(totalItemsCount / limit)"
    >
    </v-pagination>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import PageTitle from "@/components/atoms/PageTitle.vue";
import DatePicker from "@/components/atoms/DatePicker.vue";

import {
  DiaryResponse,
  NurseryAttendanceResponse,
  NurseryChildResponse,
  NurseryClassResponse,
} from "chaild-api/lib";
import { classModule } from "@/store/dataModules/classModule";
import { childDiaryModule } from "@/store/dataModules/childDiaryModule";
import LocalDataService from "@/service/LocalDataService";
import CommonUtil from "@/utils/CommonUtil";
import dayjs from "dayjs";
import { statusItems } from "@/constants/attendance";

interface DataType {
  classFilterValue: number | string | null;
  dateFilterValue: string | null;
  preDateFilterValue: string;
  isFetching: boolean;
  page: number;
}

export default defineComponent({
  name: "ChildReport",

  components: {
    PageTitle,
    DatePicker,
  },

  watch: {
    page: {
      handler(val) {
        childDiaryModule.setPage(val);
        this.search();
      },
    },
    // ⭕️ 修正5: 不要になった classFilterValue の watch を削除（クリア時は "all" に戻す処理を追加）
    classFilterValue: {
      handler(val) {
        if (!val) {
          this.classFilterValue = "all"; // クリア（×ボタン）が押されたら、"all"（すべてのクラス）に戻す
        }
      }
    }
  },

  computed: {
    classes(): NurseryClassResponse[] {
      return classModule.classes;
    },
    // ⭕️ 修正6: APIで取得したクラス一覧の先頭に「すべて」の選択肢を追加する computed を作成
    classesWithAllOption(): any[] {
      return [
        { classId: "all", className: "All Classes", year: "Common" },
        ...this.classes
      ];
    },
    // diaries(): DiaryResponse[] {
    //   return childDiaryModule.diaries;
    // },
    diaries() {
      return childDiaryModule.TemporaryDiary;
    },
    attendances(): NurseryAttendanceResponse[] {
      return childDiaryModule.attendances;
    },
    isSearchValid(): boolean {
      if (this.classFilterValue && this.dateFilterValue) {
        return true;
      } else if (this.classFilterValue == "all" && this.dateFilterValue) {
        return true;
      }
      return false;
    },
    limit(): number {
      return childDiaryModule.limit;
    },
    totalItemsCount(): number {
      return childDiaryModule.totalItemsCount;
    },
  },

  data: (): DataType => {
    return {
      classFilterValue: "all",
      dateFilterValue: dayjs().format("YYYY-MM-DD"),
      preDateFilterValue: dayjs().format("YYYY-MM-DD"),
      isFetching: false,
      page: 1,
    };
  },

  methods: {
    async search() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        this.isFetching = true;

        // 全ての園児を取得
        if (this.dateFilterValue) {
          if (this.classFilterValue == "all") {
            await childDiaryModule.AllChildList({
              date: this.dateFilterValue,
            })
          } else {
            await childDiaryModule.AllChildList({
              classId: this.classFilterValue as number,
              date: this.dateFilterValue,
            })
          }
          this.preDateFilterValue = this.dateFilterValue;
        }

        await childDiaryModule.search({
          classId:
            this.classFilterValue && this.classFilterValue !== "all"
              ? (this.classFilterValue as number)
              : undefined,
          date: this.dateFilterValue || undefined,
          nurseryId: nurseryId,
        });

        // if (
        //   this.classFilterValue &&
        //   this.dateFilterValue &&
        //   this.classFilterValue !== "all"
        // ) {
        //   await childDiaryModule.listAttendances({
        //     classId: this.classFilterValue as number,
        //     date: this.dateFilterValue,
        //   });
        // }

        if (this.dateFilterValue) {
          console.log(`this.dateFilterValue: ${this.dateFilterValue}`)
          sessionStorage.setItem('reportdate', this.dateFilterValue);
        }

        this.isFetching = false;
      }
    },
    // 検索ボタン押下時にページ１にする
    async searchBtn() {
      if (this.classFilterValue) sessionStorage.setItem('dailyreportClassFilterValue', String(this.classFilterValue)); else sessionStorage.setItem('dailyreportClassFilterValue', '');
      if (this.dateFilterValue) sessionStorage.setItem('dailyreportDateFilterValue', this.dateFilterValue); else sessionStorage.setItem('dailyreportDateFilterValue', '');
      if (this.page) sessionStorage.setItem('dailyreportPage', String(1));
      await this.search();
      this.page = 1;
    },
    async pageClick(pageNumber) {
      if (pageNumber) sessionStorage.setItem('dailyreportPage', String(pageNumber));
    },
    formatDate(value: string | null | undefined) {
      if (value) {
        return dayjs(value).format("DD/MM/YYYY");
      }
      return "";
    },
    getClassName(diary: DiaryResponse) {
      if (diary.child && diary.child.nurseryClasses) {
        const year = CommonUtil.getFiscalYear(diary.date);
        const theClass = diary.child.nurseryClasses.find(
          (c) => String(c.year) === year
        );
        if (theClass) {
          return theClass.className;
        }
      }
      return this.$t('common.unknownClass');
    },
    getAttendance(diary: DiaryResponse) {
      const find = this.attendances.find(
        (a) => a.child.childId === diary.child.childId && a.date === diary.date
      );
      if (find && find.status) {
        return statusItems.find((a) => a.value == find.status)?.text;
      }
      return "No record";
    },
    // クラス名取得
    getChildClassName(child: NurseryChildResponse) {
      if (child && child.nurseryClasses) {
        const year = CommonUtil.getFiscalYear(this.dateFilterValue || undefined);
        const theClass = child.nurseryClasses.find(
          (c) => String(c.year) === year
        );
        if (theClass) {
          return theClass.className;
        }
      }
      return this.$t('common.unknownClass');
    },
    // 登園状況取得
    getChildAttendance(child: NurseryChildResponse) {
      const find = this.attendances.find(
        (a) => a.child.childId === child.childId
      );
      if (find && find.status) {
        return statusItems.find((a) => a.value == find.status)?.text;
      }
      return "No record";
    },
    // 個人日誌新規作成
    async newDiary(childId: number, date: string) {
      this.isFetching = true;
      const content = "No notable items";
      await childDiaryModule.createChildDiary({
        childId: childId,
        date: date,
        content: content,
      })
      this.isFetching = false;
      this.$router.push({path: `/dailyreport/child/${childId}/${date}`});
    }
  },

  async mounted() {
    const nurseryId = LocalDataService.getNurseryId();
    if (nurseryId) {
      classModule.listClass({
        nurseryId: nurseryId,
      });

      const reportdate = sessionStorage.getItem('reportdate')
      console.log(`reportdate: ${reportdate}`)
      if (reportdate) {
        this.dateFilterValue = reportdate
      }

      const dailyreportClassFilterValue = sessionStorage.getItem('dailyreportClassFilterValue');
      const dailyreportDateFilterValue = sessionStorage.getItem('dailyreportDateFilterValue');
      const dailyreportPage = sessionStorage.getItem('dailyreportPage');

      if (dailyreportClassFilterValue != 'all' && dailyreportClassFilterValue != '0' && dailyreportClassFilterValue != null && dailyreportClassFilterValue != '') this.classFilterValue = Number(dailyreportClassFilterValue); else this.classFilterValue = 'all';
      if (dailyreportDateFilterValue) this.dateFilterValue = dailyreportDateFilterValue;
      if (dailyreportPage) this.page = Number(dailyreportPage);
      await this.search();
    }
  },
});
</script>